import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';

import TextField from 'src/components/TextField';
import Button from 'src/components/Button';
import { Person, Lock, Error as ErrorIcon } from 'src/components/Icon';
import { getPittaRem } from 'src/utils/cookie';
import { getApiError } from 'src/utils/apiError';
import { useLoginFlow } from 'src/features/login/hooks/useLoginFlow';
import { API_ERROR_CODES, LOGIN_CREDENTIAL_FAILURE_CODES } from 'src/constants/apiErrors';

import { Form, HelperText, FlexBetween, TextButton, CreateAccount } from '../styled';

interface LoginFormProps {
  csrfCode?: string;
  refreshCsrfCode: () => Promise<string | null>;
}

const getInitialAccount = (): string => getPittaRem()?.account ?? '';
const getInitialRemember = (): boolean => Boolean(getPittaRem()?.account);

const LoginForm = ({ csrfCode, refreshCsrfCode }: LoginFormProps) => {
  const { t } = useTranslation(['login', 'error_message']);
  const { login, isLoading, isError, error } = useLoginFlow({
    csrfCode,
    refreshCsrfCode,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [account, setAccount] = useState(getInitialAccount);
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(getInitialRemember);
  const [errorDismissed, setErrorDismissed] = useState(false);
  const apiError = getApiError(error);

  const errorMsg: string = (() => {
    if (errorDismissed || !isError) {
      return '';
    }
    if (!apiError) {
      return '';
    }
    if (LOGIN_CREDENTIAL_FAILURE_CODES.includes(apiError.code)) {
      return t('error_message:incorrect_account_or_password');
    }
    if (apiError.code === API_ERROR_CODES.EXPIRED_CSRF_CODE) {
      return t('error_message:login_page_expired_reload');
    }
    return apiError.message;
  })();

  useEffect(() => {
    if (isLoading) {
      setErrorDismissed(false);
    }
  }, [isLoading]);

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      const trimmedAccount = account.trim();
      const trimmedPassword = password.trim();
      if (!trimmedAccount || !trimmedPassword) {
        return;
      }
      try {
        await login({
          account: trimmedAccount,
          password: trimmedPassword,
          rememberMe: remember,
        });
      } catch {
        // Error display handled by errorMsg derivation above.
      }
    },
    [account, password, remember, login],
  );

  const inputErrorState = Boolean(errorMsg);

  return (
    <Form onSubmit={handleSubmit} noValidate>
      <TextField
        placeholder={t('login:account')}
        autoComplete="username"
        value={account}
        autoFocus
        inputProps={{ 'aria-label': t('login:account') }}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          setErrorDismissed(true);
          setAccount(event.target.value);
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Person />
            </InputAdornment>
          ),
        }}
        error={inputErrorState}
      />
      <TextField
        placeholder={t('login:password')}
        autoComplete="current-password"
        value={password}
        inputProps={{ 'aria-label': t('login:password') }}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          setErrorDismissed(true);
          setPassword(event.target.value);
        }}
        type={showPassword ? 'text' : 'password'}
        sx={{ marginTop: '12px' }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Lock />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                sx={{ color: '#2d2d2d' }}
                aria-label={showPassword ? t('login:hide_password') : t('login:show_password')}
                onClick={() => setShowPassword((prev) => !prev)}
                onMouseDown={(event) => event.preventDefault()}
              >
                {showPassword ? <Visibility /> : <VisibilityOff />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        error={inputErrorState}
        helperText={
          inputErrorState ? (
            <HelperText>
              <ErrorIcon />
              {errorMsg}
            </HelperText>
          ) : undefined
        }
      />
      <Button
        type="submit"
        variant="contained"
        sx={{ marginTop: '40px' }}
        disabled={isLoading || !account.trim() || !password.trim()}
      >
        {isLoading && <CircularProgress size={24} sx={{ color: '#FFF', marginRight: '8px' }} />}
        {t('login:login')}
      </Button>
      <FlexBetween>
        <FormControlLabel
          control={<Checkbox checked={remember} onChange={(_, checked) => setRemember(checked)} />}
          label={t('login:remember_me')}
          sx={{ transform: 'translateY(1px)' }}
        />
        <TextButton type="button" disabled aria-disabled="true">
          {t('login:forget_password')}
        </TextButton>
      </FlexBetween>
      <CreateAccount>
        {t('login:without_account')}
        <TextButton type="button" disabled aria-disabled="true">
          {t('login:create_account')}
        </TextButton>
      </CreateAccount>
    </Form>
  );
};

export default LoginForm;
